import * as THREE from 'three';
import { speechSprite } from './textures';

const N = 4000; // số hạt tối đa (1 draw call)
const PALETTES = [['#ff4a6a', '#ffd27a'], ['#6ad8ff', '#ffffff'], ['#b06aff', '#ff8ad8'], ['#7aff8a', '#fff27a'], ['#ff8a3a', '#ffe0a0'], ['#ffffff', '#ff4a4a']];
function dot(): THREE.Texture {
  const c = document.createElement('canvas'); c.width = c.height = 32; const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(16, 16, 0, 16, 16, 16); gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.35, 'rgba(255,255,255,.6)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 32, 32); return new THREE.CanvasTexture(c);
}

interface Shell { x: number; y: number; z: number; vx: number; vy: number; vz: number; fuse: number; effect: string; pal: string[]; seed: number }

/**
 * Pháo hoa: quả pháo bay lên (vệt sáng) rồi nổ – kiểu 'chrysanthemum' (hoa cúc), 'willow' (mưa vàng), 'heart' (trái tim).
 * Toàn bộ hạt trong MỘT Points (1 draw call), 1 PointLight dùng chung để chớp sáng (số đèn cố định ⇒ không biên dịch lại shader).
 */
export class Fireworks {
  readonly group = new THREE.Group();
  private readonly pos = new Float32Array(N * 3); private readonly col = new Float32Array(N * 3);
  private readonly vel = new Float32Array(N * 3); private readonly base = new Float32Array(N * 3);
  private readonly life = new Float32Array(N); private readonly maxLife = new Float32Array(N); private readonly drag = new Float32Array(N); private readonly grav = new Float32Array(N);
  private next = 0; private readonly shells: Shell[] = [];
  private readonly geo = new THREE.BufferGeometry(); private readonly points: THREE.Points;
  private readonly flash = new THREE.PointLight('#ffd8a0', 0, 120, 1.2); private flashT = 0;
  private readonly tmp = new THREE.Color();
  /** gọi khi nổ – để phát âm thanh (vị trí nổ) */
  onBoom?: (x: number, y: number, z: number) => void;
  onLaunch?: (x: number, z: number) => void;

  constructor(parent: THREE.Object3D) {
    this.pos.fill(0); for (let i = 0; i < N; i++) this.pos[i * 3 + 1] = -1000;
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3)); this.geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3));
    this.points = new THREE.Points(this.geo, new THREE.PointsMaterial({ size: 1.5, map: dot(), vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, sizeAttenuation: true, fog: false, toneMapped: false }));
    this.points.frustumCulled = false; this.group.add(this.points, this.flash); parent.add(this.group);
  }

  private spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, color: string, life: number, drag: number, grav: number) {
    const i = this.next; this.next = (this.next + 1) % N; const k = i * 3;
    this.pos[k] = x; this.pos[k + 1] = y; this.pos[k + 2] = z; this.vel[k] = vx; this.vel[k + 1] = vy; this.vel[k + 2] = vz;
    this.tmp.set(color); this.base[k] = this.tmp.r; this.base[k + 1] = this.tmp.g; this.base[k + 2] = this.tmp.b;
    this.life[i] = life; this.maxLife[i] = life; this.drag[i] = drag; this.grav[i] = grav;
  }

  /** Bắn một quả pháo từ mặt đất (x, z). seed: để mọi người thấy giống nhau (màn pháo hoa định kỳ). */
  launch(effect: string, x: number, z: number, groundY = 0, seed = Math.random()) {
    const r = (k: number) => { const v = Math.sin(seed * 9301 + k * 49297) * 233280; return v - Math.floor(v); };
    const pal = PALETTES[Math.floor(r(1) * PALETTES.length)];
    this.shells.push({ x, y: groundY + 0.5, z, vx: (r(2) - 0.5) * 2, vy: 21 + r(3) * 5, vz: (r(4) - 0.5) * 2, fuse: 1.05 + r(5) * 0.35, effect, pal: effect === 'willow' ? ['#ffcf5a', '#ffe9a8'] : effect === 'heart' ? ['#ff4a7a', '#ffa0c0'] : pal, seed });
    this.onLaunch?.(x, z);
  }

  private burst(s: Shell) {
    const r = (k: number) => { const v = Math.sin(s.seed * 7919 + k * 104729) * 43758.5453; return v - Math.floor(v); };
    if (s.effect === 'heart') {
      const n = 150; const yaw = Math.atan2(-s.x, -s.z + 10); // quay về phía giữa phố
      for (let i = 0; i < n; i++) {
        const t = (i / n) * Math.PI * 2; const hx = 16 * Math.sin(t) ** 3, hy = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
        const sp = 0.55; const vx = hx * sp * Math.cos(yaw), vz = hx * sp * Math.sin(yaw);
        this.spawn(s.x, s.y, s.z, vx, hy * sp, vz, s.pal[i % 2], 1.9, 1.6, 2.2);
      }
    } else if (s.effect === 'willow') {
      for (let i = 0; i < 140; i++) { const u = r(i) * 2 - 1, th = r(i + 500) * Math.PI * 2, q = Math.sqrt(1 - u * u), sp = 6 + r(i + 900) * 1.5;
        this.spawn(s.x, s.y, s.z, q * Math.cos(th) * sp, u * sp + 1, q * Math.sin(th) * sp, s.pal[i % 2], 3.2, 1.2, 3.5); }
    } else { // hoa cúc
      for (let i = 0; i < 180; i++) { const u = r(i) * 2 - 1, th = r(i + 500) * Math.PI * 2, q = Math.sqrt(1 - u * u), sp = 10 + r(i + 900) * 1.2;
        this.spawn(s.x, s.y, s.z, q * Math.cos(th) * sp, u * sp, q * Math.sin(th) * sp, s.pal[i % 2], 1.7, 2.4, 4.5); }
    }
    this.flash.position.set(s.x, s.y, s.z); this.flash.color.set(s.pal[0]); this.flashT = 0.35;
    this.onBoom?.(s.x, s.y, s.z);
  }

  update(dt: number) {
    for (let j = this.shells.length - 1; j >= 0; j--) {
      const s = this.shells[j]; s.vy -= 9.8 * dt * 0.7; s.x += s.vx * dt; s.y += s.vy * dt; s.z += s.vz * dt; s.fuse -= dt;
      this.spawn(s.x, s.y, s.z, (Math.random() - 0.5) * 0.6, -1, (Math.random() - 0.5) * 0.6, '#ffd8a0', 0.45, 2, 1); // vệt bay lên
      if (s.fuse <= 0) { this.burst(s); this.shells.splice(j, 1); }
    }
    for (let i = 0; i < N; i++) {
      const k = i * 3; if (this.life[i] <= 0) { if (this.pos[k + 1] > -999) { this.pos[k + 1] = -1000; this.col[k] = this.col[k + 1] = this.col[k + 2] = 0; } continue; }
      this.life[i] -= dt; const dr = Math.exp(-this.drag[i] * dt);
      this.vel[k] *= dr; this.vel[k + 1] = this.vel[k + 1] * dr - this.grav[i] * dt; this.vel[k + 2] *= dr;
      this.pos[k] += this.vel[k] * dt; this.pos[k + 1] += this.vel[k + 1] * dt; this.pos[k + 2] += this.vel[k + 2] * dt;
      const a = Math.max(0, this.life[i] / this.maxLife[i]); const tw = a < 0.35 ? (Math.random() < 0.5 ? a * 2.4 : a) : a; // lấp lánh khi sắp tắt
      this.col[k] = this.base[k] * tw; this.col[k + 1] = this.base[k + 1] * tw; this.col[k + 2] = this.base[k + 2] * tw;
    }
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true; (this.geo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    this.flashT = Math.max(0, this.flashT - dt); this.flash.intensity = this.flashT > 0 ? 900 * this.flashT : 0;
  }
  clear() { this.shells.length = 0; this.life.fill(0); }
  dispose() { this.geo.dispose(); (this.points.material as THREE.PointsMaterial).map?.dispose(); (this.points.material as THREE.Material).dispose(); this.group.removeFromParent(); }
}

/** Đèn trời: lồng giấy phát sáng bay lên chậm, kèm điều ước (hiện vài giây đầu). */
export class SkyLanterns {
  readonly group = new THREE.Group();
  private readonly items: Array<{ g: THREE.Group; t: number; vx: number; vz: number; label?: THREE.Sprite }> = [];
  private readonly paper = new THREE.MeshStandardMaterial({ color: '#ffd8a0', emissive: '#ff9a3a', emissiveIntensity: 1.3, transparent: true, opacity: 0.92, side: THREE.DoubleSide });
  private readonly geo = new THREE.CylinderGeometry(0.32, 0.24, 0.62, 12, 1, true);
  private readonly flameM = new THREE.MeshBasicMaterial({ color: '#fff0b0' }); private readonly flameG = new THREE.SphereGeometry(0.06, 6, 4);
  constructor(parent: THREE.Object3D) { parent.add(this.group); }
  add(x: number, y: number, z: number, text?: string | null, name?: string) {
    const g = new THREE.Group(); g.position.set(x, y + 1.6, z);
    g.add(new THREE.Mesh(this.geo, this.paper)); const f = new THREE.Mesh(this.flameG, this.flameM); f.position.y = -0.25; g.add(f);
    let label: THREE.Sprite | undefined; if (text) { label = speechSprite(`“${text}”`, name ? `Điều ước của ${name}` : undefined, '#ffe9c2', 2.8); label.position.y = 0.6; g.add(label); }
    this.group.add(g); this.items.push({ g, t: 0, vx: (Math.random() - 0.5) * 0.5, vz: (Math.random() - 0.5) * 0.5, label });
    while (this.items.length > 40) this.remove(0);
  }
  private remove(i: number) { const it = this.items[i]; if (it.label) { it.label.material.map?.dispose(); it.label.material.dispose(); } it.g.removeFromParent(); this.items.splice(i, 1); }
  update(dt: number, t: number) {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i]; it.t += dt; const up = Math.min(1.2, 0.3 + it.t * 0.08);
      it.g.position.x += (it.vx + Math.sin(t * 0.3 + i) * 0.15) * dt; it.g.position.z += it.vz * dt; it.g.position.y += up * dt;
      it.g.rotation.y += dt * 0.3; it.g.rotation.z = Math.sin(t * 1.3 + i) * 0.08;
      if (it.label && it.t > 14) { it.label.material.opacity = Math.max(0, 1 - (it.t - 14) / 3); if (it.t > 17) { it.label.removeFromParent(); it.label.material.map?.dispose(); it.label.material.dispose(); it.label = undefined; } }
      if (it.t > 110) this.remove(i);
    }
  }
  clear() { while (this.items.length) this.remove(0); }
  dispose() { this.clear(); this.paper.dispose(); this.geo.dispose(); this.flameM.dispose(); this.flameG.dispose(); this.group.removeFromParent(); }
}

/** Hiệu ứng nhỏ quanh người sau khi ăn: warm (hơi ấm bay lên), sparkle (lấp lánh), cloud (mây hồng kẹo bông), refresh (giọt mát), speed (vệt gió). */
export class BuffAura {
  private readonly n = 36; private readonly geo = new THREE.BufferGeometry(); private readonly pts: THREE.Points;
  private readonly p = new Float32Array(36 * 3); private readonly ph = new Float32Array(36);
  private effect: string | null = null; private t = 0;
  private static COLORS: Record<string, string> = { warm: '#ffb070', sparkle: '#fff2a0', cloud: '#ffb0d8', refresh: '#9ae6ff', speed: '#e8f6ff', chrysanthemum: '#fff', rise: '#ffd8a0' };
  constructor(parent: THREE.Object3D) {
    for (let i = 0; i < this.n; i++) this.ph[i] = Math.random();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.p, 3));
    this.pts = new THREE.Points(this.geo, new THREE.PointsMaterial({ size: 0.12, map: dot(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, color: '#fff' }));
    this.pts.frustumCulled = false; this.pts.visible = false; parent.add(this.pts);
  }
  reparent(parent: THREE.Object3D) { parent.add(this.pts); }
  set(effect: string | null) { this.effect = effect; this.pts.visible = !!effect; if (effect) (this.pts.material as THREE.PointsMaterial).color.set(BuffAura.COLORS[effect] ?? '#fff'); }
  update(dt: number) {
    if (!this.effect) return; this.t += dt;
    for (let i = 0; i < this.n; i++) {
      const k = i * 3; const u = (this.ph[i] + this.t * (this.effect === 'speed' ? 0.9 : 0.35)) % 1; const a = this.ph[i] * 40 + this.t * (this.effect === 'sparkle' ? 2 : 0.8);
      const r = this.effect === 'cloud' ? 0.55 + Math.sin(a) * 0.1 : 0.45;
      if (this.effect === 'speed') { this.p[k] = Math.cos(a) * 0.4; this.p[k + 1] = 0.2 + this.ph[i] * 1.4; this.p[k + 2] = -u * 1.6; }
      else if (this.effect === 'refresh') { this.p[k] = Math.cos(a) * r; this.p[k + 1] = 2 - u * 2; this.p[k + 2] = Math.sin(a) * r; }
      else { this.p[k] = Math.cos(a) * r; this.p[k + 1] = 0.2 + u * 2; this.p[k + 2] = Math.sin(a) * r; }
    }
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
  }
  dispose() { this.geo.dispose(); (this.pts.material as THREE.PointsMaterial).map?.dispose(); (this.pts.material as THREE.Material).dispose(); this.pts.removeFromParent(); }
}

/** Ánh sen: đóa sen sáng nhỏ bay lên từ bàn thờ rồi tan dần (lễ Phật / sám hối / cầu an). */
export class LotusLights {
  readonly group = new THREE.Group();
  private readonly items: Array<{ g: THREE.Group; t: number; life: number; mats: THREE.Material[] }> = [];
  private readonly petal = new THREE.SphereGeometry(0.12, 8, 6);
  private readonly tex = dot();
  constructor(parent: THREE.Object3D) { parent.add(this.group); }
  add(x: number, y: number, z: number, color = '#ffb0c8', life = 9) {
    const g = new THREE.Group(); g.position.set(x + (Math.random() - 0.5) * 0.6, y + 1.6, z + (Math.random() - 0.5) * 0.6);
    const pm = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.95, toneMapped: false });
    for (let i = 0; i < 8; i++) { const a = (i / 8) * Math.PI * 2; const p = new THREE.Mesh(this.petal, pm); p.position.set(Math.sin(a) * 0.12, 0.05, Math.cos(a) * 0.12); p.scale.set(0.6, 1.4, 0.35); p.rotation.set(-0.6, a, 0, 'YXZ'); g.add(p); }
    const core = new THREE.Mesh(this.petal, new THREE.MeshBasicMaterial({ color: '#fff2a0', transparent: true, toneMapped: false })); core.scale.setScalar(0.6); g.add(core);
    const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.tex, color, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending })); glow.scale.set(1.4, 1.4, 1); g.add(glow);
    this.group.add(g); this.items.push({ g, t: 0, life, mats: [pm, core.material as THREE.Material, glow.material] });
    while (this.items.length > 60) this.remove(0);
  }
  private remove(i: number) { const it = this.items[i]; it.mats.forEach((m) => m.dispose()); it.g.removeFromParent(); this.items.splice(i, 1); }
  update(dt: number, t: number) {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i]; it.t += dt; it.g.position.y += dt * (0.5 + it.t * 0.08); it.g.position.x += Math.sin(t * 0.7 + i) * dt * 0.2; it.g.rotation.y += dt * 0.6;
      const k = it.t > it.life - 3 ? Math.max(0, (it.life - it.t) / 3) : Math.min(1, it.t * 2);
      (it.mats[0] as THREE.MeshBasicMaterial).opacity = 0.95 * k; (it.mats[1] as THREE.MeshBasicMaterial).opacity = k; (it.mats[2] as THREE.SpriteMaterial).opacity = 0.7 * k;
      if (it.t > it.life) this.remove(i);
    }
  }
  clear() { while (this.items.length) this.remove(0); }
  dispose() { this.clear(); this.petal.dispose(); this.tex.dispose(); this.group.removeFromParent(); }
}
